import { useEffect } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Formik, useFormikContext } from "formik";
import * as Yup from "yup";

import { addAccount, getBanks, updateAccount, validateAccount } from "src/api/accounts";
import { getTags } from "src/api/settings";
import { NumberField, SelectField, TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { showSuccessToast } from "src/lib/utils";
import type { BankAccount } from "src/types/account";

const validationSchema = Yup.object().shape({
	bank_name: Yup.string().required("Bank name is required"),
	account_number: Yup.string()
		.matches(/^\d{10}$/, "Account number must be exactly 10 digits")
		.required("Account number is required"),
	account_name: Yup.string().required("Account name is required"),
	bank_code: Yup.string().required("Bank code is required"),
	priority: Yup.number().min(0, "Minimum share is zero").max(100, "Maximum share is 100").required("Percentage share is required"),
	tag_id: Yup.string().required("Tag is required"),
});

interface AccountFormValues {
	uuid?: string;
	bank_name: string;
	account_number: string;
	account_name: string;
	bank_code: string;
	priority: number | string;
	tag_id: string;
}

/** The edit form reads a few fields the list rows don't carry. */
interface EditableAccount extends BankAccount {
	bank_code?: string;
	priority?: number | string;
	tag_id?: string | number;
}

interface NewAccountModalProps {
	/** Only supplied when editing an existing bank account. */
	account?: EditableAccount;
}

const NewAccountModal = ({ account }: NewAccountModalProps) => {
	const closePortal = usePortalContext();
	const queryClient = useQueryClient();

	const initialValues: AccountFormValues = {
		...(account && { uuid: account.uuid }),
		bank_name: account?.bank_name ?? "",
		account_number: account?.account_number ?? "",
		account_name: account?.account_name ?? "",
		bank_code: account?.bank_code ?? "",
		priority: account?.priority ?? "",
		tag_id: String(account?.tag_id ?? ""),
	};

	const { mutate, isLoading } = useMutation(account ? updateAccount : addAccount, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries(["accounts"]);
			closePortal();
		},
	});

	const [{ data: rawTags }, { data: rawBanks }] = useQueries({
		queries: [
			{ queryKey: ["tags"], queryFn: () => getTags(), suspense: true },
			{ queryKey: ["banks"], queryFn: () => getBanks(), suspense: true },
		],
	});

	const tagOptions = (rawTags?.data ?? []).map((tag) => ({ value: String(tag.id ?? ""), label: tag.name || "Unknown" }));
	const bankOptions = (rawBanks?.data ?? []).map((bank) => ({ value: bank.code, label: bank.name || "Unknown" }));

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<SelectField
							label="Bank"
							name="bank_code"
							placeholder="Select a bank"
							value={values.bank_code}
							error={(touched.bank_code || touched.bank_name) && (errors.bank_code ?? errors.bank_name)}
							options={bankOptions}
							onChange={(value) => {
								setFieldValue("bank_code", value);
								setFieldValue("bank_name", bankOptions.find((bank) => bank.value === value)?.label ?? "");
							}}
							required
						/>
						<TextField
							label="Account number"
							name="account_number"
							inputMode="numeric"
							maxLength={10}
							placeholder="10-digit account number"
							value={values.account_number}
							error={touched.account_number && errors.account_number}
							onChange={handleChange}
							required
						/>
					</div>

					<ValidatedAccountName />

					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<NumberField
							label="Percentage share"
							name="priority"
							placeholder="0"
							value={values.priority}
							error={touched.priority && errors.priority}
							onChange={(value) => setFieldValue("priority", value)}
							hint="Share of each settlement paid into this account"
							required
						/>
						<SelectField
							label="Tag"
							name="tag_id"
							placeholder="Select a tag"
							value={values.tag_id}
							error={touched.tag_id && errors.tag_id}
							options={tagOptions}
							onChange={(value) => setFieldValue("tag_id", value)}
							emptyMessage="No tags yet"
							required
						/>
					</div>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? (account ? "Updating..." : "Adding...") : account ? "Update bank account" : "Add bank account"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

/**
 * Account name is never typed — it is resolved from the bank + account number and
 * shown read-only, so the user can confirm they picked the right account.
 */
const ValidatedAccountName = () => {
	const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<AccountFormValues>();

	const { data, isFetching } = useQuery({
		queryKey: ["validate-account", values.bank_code, values.account_number],
		queryFn: () => validateAccount({ accountNumber: values.account_number, bankCode: values.bank_code }),
		enabled: !!values.bank_code && values.account_number.length === 10,
	});

	const accountName = data?.data?.accountName;

	useEffect(() => {
		if (accountName) {
			setFieldValue("account_name", accountName, false);
		} else {
			setFieldValue("account_name", "", false);
			setFieldTouched("account_name", false);
		}
	}, [accountName, setFieldTouched, setFieldValue]);

	return (
		<TextField
			label="Account name"
			name={isFetching ? undefined : "account_name"}
			placeholder="Resolved from the bank and account number"
			value={isFetching ? "Checking..." : values.account_name}
			error={!isFetching && touched.account_name && errors.account_name}
			disabled
		/>
	);
};

export default NewAccountModal;
